import { MoreHoriz } from '@mui/icons-material';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { CalculsFormPerteGainsProfessionnelsActuelSaisieDirecte } from '@norma/shared/constants/calculs/calculsFormPerteGainsProfessionnelsActuelSaisieDirecte';
import { defaultRevenuEspereRow } from '@norma/shared/constants/prejudice/defaultValues/FormPerteGainsProfessionnels.defaultValues';
import { fCurrency } from '@norma/shared/helpers/formatNumber';
import {
  NumeroPieceValuesRow,
  PrejudiceFormPerteGainsProfessionnelsActuelSaisieDirecte,
  PrejudiceFormPerteGainsProfessionnelsFutursSaisieDirecte,
  unites,
} from '@norma/shared/types/prejudice.type';
import React, { useState } from 'react';
import { Control, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CalculsBox } from 'src/components/basic/CalculsBox';
import { ComputedPropsForm } from 'src/components/forms/ComputedPropsForm';
import { SelectFieldForm } from 'src/components/forms/SelectFieldForm';
import { useFormArrayUtils } from 'src/hooks/useFormArrayUtils';
import { ComputedReadFieldForm } from '../../../forms/ComputedReadFieldForm';
import { ComputedTextFieldForm } from '../../../forms/ComputedTextFieldForm';
import { TextFieldForm } from '../../../forms/TextFieldForm';
import { IMaskNumberProps, MaskNumber } from '../../../masks/MaskNumber';
import { LayoutTable, NormalTable, SpanNoWrap } from '../../../styled';
import { TooltipTableHeader } from '../../TooltipTableHeader';
import { FormNumeroPieceDialog } from '../FormNumeroPieceDialog';

interface Props {
  control: Control<
    | PrejudiceFormPerteGainsProfessionnelsActuelSaisieDirecte
    | PrejudiceFormPerteGainsProfessionnelsFutursSaisieDirecte
  >;
  isError?: boolean;
  allNumerosPieces: NumeroPieceValuesRow[];
}

export const TableRevenuEspere: React.FC<Props> = ({
  control,
  isError = false,
  allNumerosPieces,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);

  const { getValues, setValue } = useFormContext();

  const { duplicate, remove, fields, append } = useFormArrayUtils({
    control,
    name: 'revenuEspere.rows',
    getValues,
    setValue,
  });

  const handleMenuOpen =
    (index: number) => (event: React.MouseEvent<HTMLElement>) => {
      setOpenMenuIndex(index);
      setAnchorEl(event.currentTarget);
    };

  const handleMenuClose = () => {
    setOpenMenuIndex(null);
    setAnchorEl(null);
  };

  const handleDelete = (index: number) => {
    remove(index);
    handleMenuClose();
  };

  const handleDuplicate = (index: number) => {
    duplicate(index);
    handleMenuClose();
  };

  const onAppendRow = () => {
    append(defaultRevenuEspereRow);
  };
  return (
    <>
      <TableContainer component={Paper}>
        <NormalTable size="medium">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                {t('numeroPiece.form.columnHeader')}
              </TableCell>
              <TableCell align="center" sx={{ minWidth: '204px' }}>
                {t(
                  'prejudice.prejudicesFormTypes.PERTE_GAINS_PROFESSIONNELS_SAISIE_DIRECTE.fields.revenuEspere.rows.revenuEspere.columnHeader',
                )}
              </TableCell>
              <TableCell align="center" sx={{ minWidth: '204px' }}>
                <TooltipTableHeader
                  tooltipTitle={t(
                    'prejudice.perteDeChance.fields.coefficientPerteDeChance.headerTooltip',
                  )}
                  headerLabel={t(
                    'prejudice.prejudicesFormTypes.PERTE_GAINS_PROFESSIONNELS_SAISIE_DIRECTE.fields.revenuEspere.rows.coefficientPerteDeChance.columnHeader',
                  )}
                />
              </TableCell>
              <TableCell align="center" sx={{ minWidth: '204px' }}>
                {t(
                  'prejudice.prejudicesFormTypes.PERTE_GAINS_PROFESSIONNELS_SAISIE_DIRECTE.fields.revenuEspere.rows.perteDeChance.columnHeader',
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((field, index) => (
              <TableRow key={field.id}>
                <TableCell align="center">
                  <FormNumeroPieceDialog
                    control={control}
                    name={`revenuEspere.rows.${index}.numerosPieces`}
                    allOtherNumeroPieces={allNumerosPieces}
                  />
                </TableCell>
                <TableCell align="center">
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                  >
                    <TextFieldForm
                      control={control}
                      name={`revenuEspere.rows.${index}.revenuEspere`}
                      InputProps={{
                        inputComponent: MaskNumber as any,
                        inputProps: {
                          numberMask: { scale: 2 },
                          suffix: '€',
                        } as IMaskNumberProps,
                      }}
                    />
                    <SelectFieldForm
                      control={control}
                      name={`revenuEspere.rows.${index}.unite`}
                      options={unites.map((unite) => ({
                        label: t(`prejudice.fields.unite.optionsPar.${unite}`),
                        value: unite,
                      }))}
                      sx={{ minWidth: '115px' }}
                    />
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  <TextFieldForm
                    control={control}
                    name={`revenuEspere.rows.${index}.coefficientPerteDeChance`}
                    InputProps={{
                      inputComponent: MaskNumber as any,
                      inputProps: {
                        numberMask: { scale: 2, min: 0, max: 100 },
                        suffix: '%',
                      } as IMaskNumberProps,
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <ComputedReadFieldForm
                    control={control}
                    name={`revenuEspere.rows.${index}.perteDeChance`}
                    watchFields={[
                      `revenuEspere.rows.${index}.revenuEspere`,
                      `revenuEspere.rows.${index}.unite`,
                      `revenuEspere.rows.${index}.coefficientPerteDeChance`,
                    ]}
                    compute={([
                      revenuEspere,
                      unite,
                      coefficientPerteDeChance,
                    ]) => {
                      const perteDeChance =
                        CalculsFormPerteGainsProfessionnelsActuelSaisieDirecte.getPerteDeChance(
                          {
                            revenuEspere,
                            unite,
                            coefficientPerteDeChance,
                          },
                        );
                      return perteDeChance;
                    }}
                    render={(value) => (
                      <SpanNoWrap>{fCurrency(value as number)}</SpanNoWrap>
                    )}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton onClick={handleMenuOpen(index)}>
                    <MoreHoriz />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={openMenuIndex === index}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={() => handleDelete(index)}>
                      {t('forms.table.deleteRow', { index: index + 1 })}
                    </MenuItem>
                    <MenuItem onClick={() => handleDuplicate(index)}>
                      {t('forms.table.duplicateRow', { index: index + 1 })}
                    </MenuItem>
                  </Menu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={10}></TableCell>
            </TableRow>
          </TableFooter>
        </NormalTable>
      </TableContainer>
      <Stack direction="row" justifyContent="center" spacing={1}>
        <Button sx={{ alignSelf: 'center' }} onClick={onAppendRow}>
          {t('forms.table.addRow')}
        </Button>
      </Stack>
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <CalculsBox>
          <LayoutTable>
            <TableHead>
              <TableRow>
                <TableCell align="center" colSpan={2}>
                  {t(
                    'prejudice.prejudicesFormTypes.PERTE_GAINS_PROFESSIONNELS_SAISIE_DIRECTE.fields.revenuEspere.total.table.title',
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <ComputedPropsForm
                control={control}
                watchFields={['revenuEspere.total']}
                compute={([totalRevenuEspere]) => {
                  const total =
                    CalculsFormPerteGainsProfessionnelsActuelSaisieDirecte.getTotalPerteDeChanceByUnits(
                      totalRevenuEspere,
                    );
                  return {
                    props: total,
                  };
                }}
                render={(value) => {
                  const { mois, semaine, jour } = value as ReturnType<
                    typeof CalculsFormPerteGainsProfessionnelsActuelSaisieDirecte.getTotalPerteDeChanceByUnits
                  >;
                  return (
                    <>
                      <TableRow>
                        <TableCell align="left">
                          {t(
                            'prejudice.prejudicesFormTypes.PERTE_GAINS_PROFESSIONNELS_SAISIE_DIRECTE.fields.revenuEspere.total.table.jour.rowHeader',
                          )}
                        </TableCell>
                        <TableCell align="right">{fCurrency(jour)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="left">
                          {t(
                            'prejudice.prejudicesFormTypes.PERTE_GAINS_PROFESSIONNELS_SAISIE_DIRECTE.fields.revenuEspere.total.table.semaine.rowHeader',
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {fCurrency(semaine)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="left">
                          {t(
                            'prejudice.prejudicesFormTypes.PERTE_GAINS_PROFESSIONNELS_SAISIE_DIRECTE.fields.revenuEspere.total.table.mois.rowHeader',
                          )}
                        </TableCell>
                        <TableCell align="right">{fCurrency(mois)}</TableCell>
                      </TableRow>
                    </>
                  );
                }}
              />
              <TableRow>
                <TableCell align="left">
                  {t(
                    'prejudice.prejudicesFormTypes.PERTE_GAINS_PROFESSIONNELS_SAISIE_DIRECTE.fields.revenuEspere.total.table.annee.rowHeader',
                  )}
                </TableCell>
                <TableCell align="right">
                  <ComputedTextFieldForm
                    control={control}
                    watchFields={['revenuEspere.rows']}
                    compute={([rows]) =>
                      CalculsFormPerteGainsProfessionnelsActuelSaisieDirecte.getTotalPerteDeChance(
                        rows,
                      )
                    }
                    name={'revenuEspere.total'}
                    InputProps={{
                      inputComponent: MaskNumber as any,
                      inputProps: {
                        numberMask: { scale: 2 },
                        suffix: '€',
                      } as IMaskNumberProps,
                    }}
                    editedFieldsName="editedFields"
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </LayoutTable>
        </CalculsBox>
      </Stack>
      {isError && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent={'center'}
        >
          <Typography color="error" align={'center'} fontWeight={'bold'}>
            {t(
              'prejudice.prejudicesFormTypes.PERTE_GAINS_PROFESSIONNELS_SAISIE_DIRECTE.stepper.error.requiredStep',
            )}
          </Typography>
        </Stack>
      )}
    </>
  );
};
